import { PaziaBreadcrumbs } from "components/atoms";
import { routes } from "consts";
import { COURSE_STATUS_NAMES, COURSE_STATUS } from "consts/courseType";
import { useCourseQuery } from "pages/CourseCreate/useCourseQuery";
import { CourseDocumentsPage } from "pages/Course/Documents/CourseDocumentsPage";
import { CourseLectureConfirmationsPage } from "pages/Course/CourseLectureConfirmationsPage";
import { PaziaLoader } from "components/atoms";
import { Link, Route, Routes, useParams, useLocation } from "react-router-dom";
import {
  IoDocuments,
  IoInformationCircle,
  IoCalendar,
  IoPeople,
  IoSettings,
  IoStatsChart,
  IoWallet,
  IoCheckmarkCircle,
} from "react-icons/io5";

const sidebarItems = [
  {
    title: "Dokumenty",
    path: (courseId: string = ":courseId") => routes.COURSE_PAGE.DOCUMENTS(courseId),
    icon: IoDocuments,
    description: "Zarządzaj dokumentami kursu",
  },
  {
    title: "Potwierdzenia obecności",
    path: (courseId: string = ":courseId") => routes.COURSE_PAGE.LECTURE_CONFIRMATIONS(courseId),
    icon: IoCheckmarkCircle,
    description: "Kto z trenerów potwierdził obecność",
  },
  // Add more navigation items here when they become available
  // {
  //   title: "Studenci",
  //   path: (courseId: string = ":courseId") => `/course/${courseId}/students`,
  //   icon: IoPeople,
  //   description: "Lista uczestników kursu"
  // },
  // {
  //   title: "Statystyki",
  //   path: (courseId: string = ":courseId") => `/course/${courseId}/stats`,
  //   icon: IoStatsChart,
  //   description: "Statystyki i raporty"
  // },
] as const;

// Helper function to get status styling
const getStatusStyle = (status: number) => {
  switch (status) {
    case COURSE_STATUS.DRAFT:
      return {
        name: COURSE_STATUS_NAMES[COURSE_STATUS.DRAFT],
        className: "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300",
      };
    case COURSE_STATUS.PLANNED:
      return {
        name: COURSE_STATUS_NAMES[COURSE_STATUS.PLANNED],
        className: "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300",
      };
    case COURSE_STATUS.READY:
      return {
        name: COURSE_STATUS_NAMES[COURSE_STATUS.READY],
        className: "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300",
      };
    case COURSE_STATUS.ONGOING:
      return {
        name: COURSE_STATUS_NAMES[COURSE_STATUS.ONGOING],
        className: "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300",
      };
    case COURSE_STATUS.ARCHIVED:
      return {
        name: COURSE_STATUS_NAMES[COURSE_STATUS.ARCHIVED],
        className: "bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-300",
      };
    default:
      return {
        name: "Nieznany",
        className: "bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-300",
      };
  }
};

export const CoursePage = () => {
  const { courseId } = useParams();
  const location = useLocation();

  const { isLoading, course } = useCourseQuery(courseId);

  if (isLoading) return <PaziaLoader position="center" />;
  if (!course) return null;

  const statusInfo = getStatusStyle(course.data.status);

  return (
    <div>
      <PaziaBreadcrumbs
        className="pl-4 pt-4"
        segments={[
          { title: "Grupy kursów", path: routes.COURSE_GROUPS },
          { title: course.group.name, path: `${routes.COURSE_GROUPS_EDIT_COURSES(course.group.id.toString())}` },
          {
            title: [course.data.edition, course.data.variant].filter(Boolean).join(" · "),
            path: routes.COURSES_MANAGE(courseId),
          },
          { title: "Edycja kursu" },
        ]}
      />

      {/* Full screen height layout container */}
      {/* Phones: the sidebar stacks above the content (side by side left the content ~80 px wide) */}
      <div className="mt-4 flex flex-col md:h-[calc(100vh-140px)] md:flex-row">
        {/* Enhanced sidebar */}
        <div className="w-full flex-shrink-0 border-b border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-900 md:w-72 md:border-b-0 md:border-r">
          <div className="flex h-full flex-col">
            {/* Course header section */}
            <div className="border-b border-gray-200 bg-gradient-to-r from-indigo-50 to-blue-50 p-6 dark:border-gray-700 dark:from-indigo-900/30 dark:to-blue-900/30">
              <div className="flex items-start space-x-3">
                <div className="min-w-0 flex-1">
                  <h3 className="truncate text-lg font-semibold text-gray-900 dark:text-white">{course.data.name}</h3>
                  <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">{course.data.edition}</p>
                  <div className="mt-2 flex items-center space-x-2">
                    <span className="text-xs text-gray-500 dark:text-gray-500">Grupa: {course.group.name}</span>
                  </div>
                  <div>
                    <span className="text-xs text-gray-500 dark:text-gray-500">
                      Status:
                      <span
                        className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${statusInfo.className}`}
                      >
                        {statusInfo.name}
                      </span>
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Course stats/info section */}
            <div className="border-b border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-800/50">
              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-lg bg-white p-3 text-center shadow-sm dark:bg-gray-800">
                  <IoPeople className="mx-auto mb-1 h-5 w-5 text-gray-600 dark:text-gray-400" />
                  <div className="text-sm font-medium text-gray-900 dark:text-white">
                    {course.data.numberOfStudents}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">Kursantów</div>
                </div>
                {/* <div className="rounded-lg bg-white p-3 text-center shadow-sm dark:bg-gray-800">
                  <IoCalendar className="mx-auto mb-1 h-5 w-5 text-gray-600 dark:text-gray-400" />
                  <div className="text-sm font-medium text-gray-900 dark:text-white">
                    {course.tranches?.length || 0}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">Transzy</div>
                </div> */}
              </div>
            </div>

            {/* Navigation section */}
            <div className="flex-1 p-4">
              <div className="space-y-2">
                <h4 className="mb-3 text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                  Zarządzanie
                </h4>
                {sidebarItems.map((item) => {
                  const isActive = location.pathname.includes(item.path(courseId).split("/").pop() || "");
                  const IconComponent = item.icon;

                  return (
                    <Link
                      key={item.title}
                      to={item.path(courseId)}
                      className={`group flex items-center rounded-lg p-3 transition-all duration-200 ${
                        isActive
                          ? "border border-indigo-200 bg-indigo-50 text-indigo-700 dark:border-indigo-700 dark:bg-indigo-900/50 dark:text-indigo-300"
                          : "text-gray-700 hover:bg-gray-50 hover:text-indigo-600 dark:text-gray-300 dark:hover:bg-gray-800 dark:hover:text-indigo-400"
                      }`}
                    >
                      <div
                        className={`flex-shrink-0 rounded p-1 ${
                          isActive
                            ? "bg-indigo-100 dark:bg-indigo-800"
                            : "bg-gray-100 group-hover:bg-indigo-50 dark:bg-gray-700 dark:group-hover:bg-indigo-900/30"
                        }`}
                      >
                        <IconComponent
                          className={`h-4 w-4 ${
                            isActive
                              ? "text-indigo-600 dark:text-indigo-400"
                              : "text-gray-600 group-hover:text-indigo-500 dark:text-gray-400 dark:group-hover:text-indigo-400"
                          }`}
                        />
                      </div>
                      <div className="ml-3 flex-1">
                        <div
                          className={`text-sm font-medium ${
                            isActive
                              ? "text-indigo-700 dark:text-indigo-300"
                              : "text-gray-900 group-hover:text-indigo-600 dark:text-white dark:group-hover:text-indigo-400"
                          }`}
                        >
                          {item.title}
                        </div>
                        <div className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">{item.description}</div>
                      </div>
                      {isActive && <div className="h-8 w-1 rounded-full bg-indigo-500 dark:bg-indigo-400"></div>}
                    </Link>
                  );
                })}
              </div>
            </div>

            {/* Footer section */}
            <div className="border-t border-gray-200 p-4 dark:border-gray-700">
              <Link
                to={routes.COURSES_MANAGE(courseId)}
                className="flex w-full items-center justify-center rounded-lg p-2 text-sm text-gray-600 transition-colors duration-200 hover:bg-gray-50 hover:text-indigo-600 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-indigo-400"
              >
                <IoSettings className="mr-2 h-4 w-4" />
                Ustawienia kursu
              </Link>
            </div>
          </div>
        </div>

        {/* Scrollable content area */}
        <div className="min-w-0 flex-1 overflow-y-auto bg-white dark:bg-gray-900">
          <div className="h-full p-6">
            {/* Main content will go here */}
            <div className="space-y-4">
              <Routes>
                <Route path={routes.COURSE_PAGE.SUBPAGE.DOCUMENTS} element={<CourseDocumentsPage />} />
                <Route path={routes.COURSE_PAGE.SUBPAGE.LECTURE_CONFIRMATIONS} element={<CourseLectureConfirmationsPage />} />
              </Routes>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
